import React, { useState, useEffect } from 'react';
import apiClient, { API_ENDPOINTS } from '../services/api';
import Captcha, { validateCaptcha } from './Captcha';
import { obfuscateEmail } from '../utils/emailObfuscation';
import Toast from './Toast';
import FilePreviewDialog from './dialogs/FilePreviewDialog';

const VigilanceComplaintForm = ({ onSubmit, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Information - will be pre-filled from localStorage
    name: '',
    mobileNumber: '',
    email: '',
    address: '',
    verificationCode: '',

    // Complaint Information
    complaintDetails: '',
    supportingDocuments: [],
    generalDocuments: [],
  });

  // Function to load user data from localStorage
  const loadUserData = () => {
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      try {
        const parsedData = JSON.parse(storedUserData);
        setUserData(parsedData);

        // Pre-fill form with user data
        setFormData(prev => ({
          ...prev,
          name: parsedData.name || '',
          mobileNumber: parsedData.mobile || parsedData.mobileNumber || '',
          email: parsedData.email || '',
        }));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  };

  // Load user data from localStorage on component mount
  useEffect(() => {
    loadUserData();
  }, []);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [complaintNumber, setComplaintNumber] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Refresh CAPTCHA when moving to step 3 (Preview & Submit)
  useEffect(() => {
    if (currentStep === 3) {
      setRefreshTrigger(prev => prev + 1);
      setCaptchaInput('');
    }
  }, [currentStep]);

  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    fileUrl: '',
    fileName: '',
    fileType: ''
  });

  const handlePreviewFile = (file) => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setPreviewDialog({
        isOpen: true,
        fileUrl: url,
        fileName: file.name,
        fileType: file.type
      });
    }
  };

  const closePreviewDialog = () => {
    // Revoke object URL if it was created locally
    if (previewDialog.fileUrl && previewDialog.fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewDialog.fileUrl);
    }
    setPreviewDialog({ ...previewDialog, isOpen: false });
  };

  const steps = [
    { id: 1, title: 'Basic Information', description: 'Personal details and verification' },
    { id: 2, title: 'Complaint Information', description: 'Complaint details and documents' },
    { id: 3, title: 'Preview & Submit', description: 'Review and submit complaint' }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      // Note: name, mobileNumber, and email are pre-filled from localStorage and read-only
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      // Verification code is optional for now
    }

    if (step === 2) {
      if (!formData.complaintDetails.trim()) newErrors.complaintDetails = 'Complaint details are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (type, files) => {
    const fileArray = Array.from(files);
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const validFiles = [];
    const fileErrors = [];

    fileArray.forEach((file) => {
      if (file.size > maxSize) {
        fileErrors.push(`${file.name}: File size exceeds 5MB limit`);
      } else {
        validFiles.push(file);
      }
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({
        ...prev,
        fileUpload: fileErrors.join(', ')
      }));
    } else {
      // Clear any previous file upload errors
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.fileUpload;
        return newErrors;
      });
    }

    if (validFiles.length > 0) {
      setFormData(prev => ({ ...prev, [type]: [...prev[type], ...validFiles] }));
    }
  };

  const handleRemoveFile = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    // Validate CAPTCHA
    const captchaValidation = validateCaptcha(captchaInput);
    if (!captchaValidation.isValid) {
      setErrors(prev => ({
        ...prev,
        captcha: captchaValidation.error
      }));
      return;
    }

    if (!captchaKey) {
      setErrors(prev => ({
        ...prev,
        captcha: 'CAPTCHA key is missing. Please refresh the CAPTCHA.'
      }));
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    try {
      // Create FormData for file upload
      const formDataObj = new FormData();

      // Add complaint type
      formDataObj.append('complaint_type', 'online');

      // Add your_complaint (complaint details)
      formDataObj.append('your_complaint', formData.complaintDetails);

      // Add address
      formDataObj.append('address', formData.address);

      // Add CAPTCHA and CAPTCHA key
      formDataObj.append('captcha', captchaInput.trim());
      formDataObj.append('captcha_key', captchaKey);

      // Add supporting documents
      if (formData.supportingDocuments && formData.supportingDocuments.length > 0) {
        formData.supportingDocuments.forEach((file) => {
          formDataObj.append('supporting_documents[]', file);
        });
      }

      // Add general documents
      if (formData.generalDocuments && formData.generalDocuments.length > 0) {
        formData.generalDocuments.forEach((file) => {
          formDataObj.append('general_documents[]', file);
        });
      }

      // Call the API endpoint
      const response = await apiClient.post(
        API_ENDPOINTS.user.createOnline,
        formDataObj,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Extract complaint number from response
      const complaintNumber = response.data?.complaint_number ||
        response.data?.complaintNumber ||
        response.data?.data?.complaint_number ||
        `VIG${Date.now().toString().slice(-6)}`;

      setComplaintNumber(complaintNumber);

      // Show success toast
      setToast({
        isVisible: true,
        message: response.data?.message || 'Complaint submitted successfully!',
        type: 'success'
      });

      // Call parent onSubmit with complaint data
      onSubmit({
        ...formData,
        complaintNumber,
        submittedAt: new Date().toISOString()
      });

      // Reset CAPTCHA after successful submission
      setCaptchaInput('');
      setCaptchaKey('');
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error submitting complaint:', error);

      // Extract error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to submit complaint. Please try again.';

      // Show error toast
      setToast({
        isVisible: true,
        message: errorMessage,
        type: 'error'
      });

      // Also set error in state for inline display
      setErrors({
        general: errorMessage
      });

      // Refresh CAPTCHA on all failure attempts
      setRefreshTrigger(prev => prev + 1);
      setCaptchaInput('');
      setCaptchaKey('');

      setIsSubmitting(false);
    }
  };


  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors duration-300 ${currentStep >= step.id
            ? 'bg-primary-600 border-primary-600 text-white'
            : 'border-gray-300 text-gray-500'
            }`}>
            <span className="text-sm font-semibold">{step.id}</span>
          </div>
          <div className="ml-3 text-left">
            <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-primary-600' : 'text-gray-500'
              }`}>
              {step.title}
            </p>
            <p className="text-xs text-gray-400">{step.description}</p>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-16 h-0.5 mx-4 ${currentStep > step.id ? 'bg-primary-600' : 'bg-gray-300'
              }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderBasicInfoStep = () => (
    <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Information</h2>

      <div className="space-y-6">
        {/* Name - Read-only */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            type="text"
            id="name"
            autoComplete="off"
            value={formData.name}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
            placeholder="Your name (pre-filled from account)"
          />
        </div>

        {/* Mobile Number - Read-only */}
        <div>
          <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-2">
            Mobile Number
          </label>
          <input
            type="tel"
            id="mobileNumber"
            autoComplete="off"
            value={formData.mobileNumber}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
            placeholder="Your mobile number (pre-filled from account)"
          />
        </div>

        {/* Email - Read-only */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            autoComplete="off"
            value={formData.email}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
            placeholder="Your email (pre-filled from account)"
          />
        </div>

        {/* Address */}
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
            Address *
          </label>
          <textarea
            id="address"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            rows={3}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.address ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Enter your address."
          />
          {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors duration-300"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-300"
        >
          Next
        </button>
      </div>
    </form>
  );

  const renderComplaintInfoStep = () => (
    <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Complaint Information</h2>

      <div className="space-y-6">
        {/* Complaint Details */}
        <div>
          <label htmlFor="complaintDetails" className="block text-sm font-medium text-gray-700 mb-2">
            Your Complaint *
          </label>
          <textarea
            id="complaintDetails"
            value={formData.complaintDetails}
            onChange={(e) => handleInputChange('complaintDetails', e.target.value)}
            rows={6}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.complaintDetails ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Describe your complaint or suggestion in detail...."
          />
          {errors.complaintDetails && <p className="mt-1 text-sm text-red-600">{errors.complaintDetails}</p>}
        </div>

        {/* Supporting Documents */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supporting Documents
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors duration-300">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-gray-600 mb-2">Attach supporting documents</p>
              <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG (Max 5MB each)</p>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload('supportingDocuments', e.target.files)}
                className="mt-4 hidden"
                id="supportingDocs"
              />
              <label
                htmlFor="supportingDocs"
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 transition-colors duration-300"
              >
                Choose Files
              </label>
            </div>
          </div>
          {formData.supportingDocuments.length > 0 && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Selected files:</p>
              <div className="space-y-2">
                {formData.supportingDocuments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 hover:border-primary-300 transition-colors">
                    <div className="flex items-center flex-1 min-w-0">
                      <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-900 truncate">{file.name}</span>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        ({(file.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePreviewFile(file)}
                      className="ml-3 flex-shrink-0 p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      title="View file"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('supportingDocuments', index)}
                      className="ml-3 flex-shrink-0 p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title="Remove file"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {errors.fileUpload && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.fileUpload}</p>
            </div>
          )}
        </div>

        {/* General Documents */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            General Documents
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors duration-300">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-gray-600 mb-2">Attach general documents</p>
              <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG (Max 5MB each)</p>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload('generalDocuments', e.target.files)}
                className="mt-4 hidden"
                id="generalDocs"
              />
              <label
                htmlFor="generalDocs"
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 transition-colors duration-300"
              >
                Choose Files
              </label>
            </div>
          </div>
          {formData.generalDocuments.length > 0 && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Selected files:</p>
              <div className="space-y-2">
                {formData.generalDocuments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 hover:border-primary-300 transition-colors">
                    <div className="flex items-center flex-1 min-w-0">
                      <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-900 truncate">{file.name}</span>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        ({(file.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePreviewFile(file)}
                      className="ml-3 flex-shrink-0 p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      title="View file"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('generalDocuments', index)}
                      className="ml-3 flex-shrink-0 p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title="Remove file"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {errors.fileUpload && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.fileUpload}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <button
          type="button"
          onClick={handlePrevious}
          className="px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors duration-300"
        >
          Previous
        </button>
        <button
          type="submit"
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-300"
        >
          Next
        </button>
      </div>
    </form>
  );

  const renderPreviewStep = () => (
    <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Complaint</h2>
      <p className="text-gray-600 mb-8">Please review all information before submitting your complaint</p>

      {errors.general && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errors.general}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Basic Information Review */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-300"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="font-medium text-gray-900">{formData.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-900">{obfuscateEmail(formData.email)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Address</p>
              <p className="font-medium text-gray-900">{formData.address}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Mobile Number</p>
              <p className="font-medium text-gray-900">{formData.mobileNumber}</p>
            </div>
          </div>
        </div>

        {/* Complaint Information Review */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Complaint Information</h3>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-300"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">Complaint Details</p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-gray-900">{formData.complaintDetails}</p>
            </div>

            {/* Supporting Documents */}
            {formData.supportingDocuments && formData.supportingDocuments.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Supporting Documents</p>
                <div className="space-y-2">
                  {formData.supportingDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center flex-1 min-w-0">
                        <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-900 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          ({(file.size / 1024).toFixed(2)} KB)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* General Documents */}
            {formData.generalDocuments && formData.generalDocuments.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">General Documents</p>
                <div className="space-y-2">
                  {formData.generalDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center flex-1 min-w-0">
                        <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-900 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          ({(file.size / 1024).toFixed(2)} KB)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CAPTCHA */}
      <div className="mt-6">
        <Captcha
          value={captchaInput}
          onChange={(e) => {
            setCaptchaInput(e.target.value);
            if (errors.captcha) {
              setErrors(prev => ({ ...prev, captcha: '' }));
            }
          }}
          error={errors.captcha}
          disabled={isSubmitting}
          onCaptchaChange={(captchaData) => {
            if (captchaData && captchaData.key) {
              setCaptchaKey(captchaData.key);
            }
          }}
          refreshTrigger={refreshTrigger}
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <button
          type="button"
          onClick={handlePrevious}
          className="flex items-center px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors duration-300"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Edit
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`px-6 py-3 bg-green-600 text-white rounded-lg font-semibold transition-colors duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
            }`}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Complaint'}
        </button>
      </div>
    </form>
  );

  const renderSuccessMessage = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Complaint Submitted Successfully!</h2>
        <p className="text-gray-600 mb-6">Your vigilance complaint has been submitted successfully.</p>
      </div>

      <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-6">
        <div className="text-sm text-primary-700">
          <p className="mb-2">✓ Check your userRegistered email for complaint number</p>
          <p className="mb-2">✓ Check your userRegistered phone for SMS with complaint details</p>
          <p>✓ Check the status of your complaint on the dashboard using the complaint number</p>
        </div>
      </div>

      <div className="space-y-4">
        <button
          onClick={() => {
            setCurrentStep(1);
            setComplaintNumber('');
            setFormData({
              name: '',
              mobileNumber: '',
              email: '',
              address: '',
              verificationCode: '',
              complaintDetails: '',
              supportingDocuments: [],
              generalDocuments: [],
            });
            setErrors({});
            // Reload user data from localStorage
            loadUserData();
          }}
          className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-300"
        >
          Submit Another Complaint
        </button>
        <button
          onClick={onCancel}
          className="w-full px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors duration-300"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Step Indicator */}
      {!complaintNumber && renderStepIndicator()}

      {/* Form Content */}
      <div className="mt-8">
        {complaintNumber ? (
          renderSuccessMessage()
        ) : (
          <>
            {currentStep === 1 && renderBasicInfoStep()}
            {currentStep === 2 && renderComplaintInfoStep()}
            {currentStep === 3 && renderPreviewStep()}
          </>
        )}
      </div>

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'success' })}
      />

      <FilePreviewDialog
        isOpen={previewDialog.isOpen}
        onClose={closePreviewDialog}
        fileUrl={previewDialog.fileUrl}
        fileName={previewDialog.fileName}
        fileType={previewDialog.fileType}
      />
    </div>
  );
};

export default VigilanceComplaintForm;
